import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { Animated, Platform, Pressable, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { Dance } from "@recital-master/shared";
import { danceStyleLabel, formatDuration } from "@recital-master/shared";

import {
  FullscreenExitIcon,
  FullscreenIcon,
  NextIcon,
  PauseIcon,
  PictureInPictureIcon,
  PlayIcon,
  PreviousIcon,
  SeekBackwardIcon,
  SeekForwardIcon,
  VideoFillIcon,
  VideoFitIcon
} from "@/design/icons";
import { Muted, Overline, Text } from "@/design/Text";
import { gradients, palette, radius, space } from "@/design/tokens";
import { displayTitle, joinWithDot } from "@/lib/format";
import { usePlayerChromeOpacity } from "@/playback/usePlayerChromeVisibility";
import { useSmoothPlaybackProgress } from "@/playback/useSmoothPlaybackProgress";

export type PlayerChromeProps = {
  dance: Dance | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  visible: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: (seconds?: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onPictureInPicture?: () => void;
  /** Opens the playback settings sheet (speed / audio tracks). */
  onOpenSettings?: () => void;
  /** Presentation state only; landscape composition is intentionally separate. */
  isFullscreen?: boolean;
  onFullscreen?: () => void;
  onExitFullscreen?: () => void;
  /** Native fullscreen video scaling: complete frame or edge-to-edge crop. */
  videoContentFit?: "contain" | "cover";
  onToggleVideoContentFit?: () => void;
  /** Notify the owner the user is interacting (resets its auto-hide timer). */
  onInteraction?: () => void;
  /** Phone-width player: tighter paddings, smaller controls. */
  compact?: boolean;
  /** TV owns presentation; this device renders the Remote (no local utilities). */
  remotePlayback?: boolean;
  /** The Remote's identity line, e.g. "Playing on Living Room TV". */
  remoteEyebrow?: string;
  /** Current rate shown on the speed chip; null hides it (casting). */
  playbackRate?: number | null;
  progressSampleIntervalMs?: number;
  hideTitle?: boolean;
};

function ControlButton({
  Icon,
  label,
  onPress,
  disabled = false,
  iconSize = 20,
  size = 42,
  circled = false,
  veiled = false
}: {
  Icon: ComponentType<{ size?: number; color?: string }>;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  iconSize?: number;
  size?: number;
  /** Fullscreen treatment: hairline ring over barely-there glass. */
  circled?: boolean;
  /** Remote-panel treatment: the backdrop is already dark, so the ring goes
      cool and the glass nearly disappears — presence, not a chip. */
  veiled?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed, hovered, focused }) => [
        styles.controlButton,
        size !== 42 && { height: size, width: size },
        circled && styles.controlButtonCircled,
        veiled && styles.controlButtonVeiled,
        (hovered || focused) &&
          (veiled ? styles.controlButtonVeiledHovered : styles.controlButtonHovered),
        pressed && { opacity: 0.7 },
        disabled && { opacity: 0.3 }
      ]}
    >
      <Icon size={iconSize} color={palette.text} />
    </Pressable>
  );
}

/** The speed control is its own label — "1×" beats a junk-drawer gear. */
function RateButton({ rate, onPress }: { rate: number; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Playback speed ${rate}×`}
      onPress={onPress}
      style={({ pressed, hovered, focused }) => [
        styles.controlButton,
        (hovered || focused) && styles.controlButtonHovered,
        pressed && { opacity: 0.7 }
      ]}
    >
      <Muted variant="smallStrong" color={palette.text} style={styles.rateLabel}>
        {`${rate}×`}
      </Muted>
    </Pressable>
  );
}

/** Custom playback controls — the app's own chrome, brand accents only. */
export function PlayerChrome({
  dance,
  playing,
  currentTime,
  duration,
  visible,
  hasPrevious,
  hasNext,
  onTogglePlay,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onPrevious,
  onNext,
  onPictureInPicture,
  onOpenSettings,
  isFullscreen = false,
  onFullscreen,
  onExitFullscreen,
  videoContentFit = "contain",
  onToggleVideoContentFit,
  onInteraction,
  compact = false,
  remotePlayback = false,
  remoteEyebrow,
  playbackRate = null,
  progressSampleIntervalMs = 500,
  hideTitle = false
}: PlayerChromeProps) {
  const insets = useSafeAreaInsets();
  const eyebrow = dance
    ? joinWithDot([danceStyleLabel(dance), dance.className])
    : "";
  const seekWidth = useRef(0);

  // Scrubbing: the bar becomes a responder so it tracks the pointer through
  // a full drag (grant → move → release), not just a tap. The video seeks
  // live during the drag so the frame previews the target (120ms cadence),
  // then commits the exact position on release. Native temporarily enables
  // expo-video's tolerant optimized scrubbing mode. The chrome
  // is pinned visible for the whole gesture. After release the
  // scrub value keeps driving the bar until fresh playback time arrives, so
  // there's no flash of the stale position.
  const [scrub, setScrub] = useState<{ fraction: number; live: boolean } | null>(null);
  const lastScrubSeek = useRef(0);
  const scrubActive = useRef(false);
  // Screen-space left edge of the track, captured on grant. Android reports
  // locationX relative to whichever CHILD the finger is currently over (the
  // 13px handle, the fill bar…), so mid-drag it can collapse to ~0 and snap
  // the thumb to the far left. pageX is stable screen space; anchor to it.
  const trackPageLeft = useRef(0);
  const onScrubEndRef = useRef(onScrubEnd);
  onScrubEndRef.current = onScrubEnd;
  const fractionAt = (pageX: number) =>
    seekWidth.current > 0
      ? Math.min(1, Math.max(0, (pageX - trackPageLeft.current) / seekWidth.current))
      : 0;
  const smoothProgress = useSmoothPlaybackProgress({
    currentTime,
    duration,
    itemKey: dance?.danceId ?? null,
    playing,
    sampleIntervalMs: progressSampleIntervalMs
  });
  const shownProgress = scrub
    ? (`${scrub.fraction * 100}%` as `${number}%`)
    : smoothProgress.interpolate({
        inputRange: [0, 1],
        outputRange: ["0%", "100%"]
      });
  const shownTime =
    Platform.OS !== "web" && scrub && duration > 0
      ? scrub.fraction * duration
      : currentTime;
  const chromeShown = visible || scrub?.live === true;
  const opacity = usePlayerChromeOpacity(chromeShown);
  const fullscreenAction = isFullscreen ? onExitFullscreen : onFullscreen;
  const fullscreenLabel = isFullscreen ? "Exit fullscreen" : "Fullscreen";
  const seekBy = (seconds: number) => {
    const requestedTime = currentTime + seconds;
    if (seconds > 0 && duration > 0 && requestedTime >= duration) {
      console.log(
        `[play] seek-button boundary=next current=${currentTime.toFixed(2)} duration=${duration.toFixed(2)}`
      );
      onInteraction?.();
      onNext();
      return;
    }
    const target = Math.max(
      0,
      Math.min(duration || Number.POSITIVE_INFINITY, requestedTime)
    );
    console.log(
      `[play] seek-button delta=${seconds} current=${currentTime.toFixed(2)} target=${target.toFixed(2)} duration=${duration.toFixed(2)}`
    );
    onInteraction?.();
    onSeek(target);
  };

  useEffect(() => {
    setScrub((value) => (value && !value.live ? null : value));
  }, [currentTime]);

  useEffect(
    () => () => {
      if (scrubActive.current) onScrubEndRef.current?.();
    },
    []
  );

  const scrubTo = (pageX: number, commit: boolean) => {
    const fraction = fractionAt(pageX);
    setScrub({ fraction, live: !commit });
    onInteraction?.();
    if (duration <= 0) {
      if (commit) onScrubEnd?.();
      return;
    }
    const now = Date.now();
    if (commit) {
      lastScrubSeek.current = now;
      // Keep the presentation clock on the committed thumb even while paused;
      // the native/receiver timestamp remains the source of truth underneath.
      smoothProgress.stopAnimation();
      smoothProgress.setValue(fraction);
      // The owner tears down tolerant native scrubbing before committing the
      // exact position, preventing release from snapping to a keyframe.
      onScrubEnd?.(fraction * duration);
    } else if (Platform.OS === "web" && now - lastScrubSeek.current > 120) {
      lastScrubSeek.current = now;
      onSeek(fraction * duration);
    } else if (Platform.OS !== "web" && now - lastScrubSeek.current > 120) {
      lastScrubSeek.current = now;
      onSeek(fraction * duration);
    }
  };

  return (
    <Animated.View
      onTouchStart={Platform.OS === "web" ? undefined : onInteraction}
      style={[
        StyleSheet.absoluteFill,
        styles.chromeLayer,
        { opacity, pointerEvents: chromeShown ? "box-none" : "none" }
      ]}
    >
      {/* Title scrim */}
      <LinearGradient
        colors={["rgba(0,0,0,0.6)", "rgba(0,0,0,0)"]}
        style={[styles.topScrim, { pointerEvents: "none" }]}
      />
      {/* Inline players never repeat the dance title — the Now Playing card
          directly beneath owns identity. Fullscreen has no card, so the
          title lives up here — top-center, between the feed chip (left)
          and the route pill (right). */}
      {dance && !hideTitle && isFullscreen ? (
        <View
          style={[
            styles.titleBlock,
            { pointerEvents: "none", top: space.xl + insets.top }
          ]}
        >
          {eyebrow ? <Overline color={palette.textSoft}>{eyebrow}</Overline> : null}
          <Text
            variant="title"
            color={palette.text}
            numberOfLines={1}
            style={styles.titleCentered}
          >
            {displayTitle(dance.title)}
          </Text>
        </View>
      ) : null}

      {/* Bottom controls */}
      <LinearGradient
        colors={["rgba(0,0,0,0)", "rgba(0,0,0,0.82)"]}
        style={[styles.bottomScrim, { pointerEvents: "none" }]}
      />
      <View
        style={[
          styles.controls,
          compact && styles.controlsCompact,
          remotePlayback && !isFullscreen &&
            (compact ? styles.controlsRemoteCompact : styles.controlsRemote),
          isFullscreen && {
            bottom: space.md + insets.bottom,
            left: space.xl + insets.left,
            right: space.xl + insets.right
          }
        ]}
      >
        {/* Universal signature at every size: [current] ——— rail ——— [duration]. */}
        <View style={styles.seekRow}>
          <Muted
            variant="smallStrong"
            color={palette.text}
            style={[styles.seekTime, styles.seekTimeLeft]}
          >
            {formatDuration(shownTime)}
          </Muted>
          <View style={styles.seekBar}>
            <View
              accessibilityRole="adjustable"
              accessibilityLabel="Seek"
              onLayout={(event) => {
                seekWidth.current = event.nativeEvent.layout.width;
              }}
              onStartShouldSetResponder={() => true}
              onMoveShouldSetResponder={() => true}
              onMoveShouldSetResponderCapture={Platform.OS === "web" ? undefined : () => true}
              onResponderTerminationRequest={() => false}
              onResponderGrant={(event) => {
                scrubActive.current = true;
                // At grant the touch target IS the event target, so locationX is
                // reliable exactly once — use it to anchor the track's screen X.
                trackPageLeft.current =
                  event.nativeEvent.pageX - event.nativeEvent.locationX;
                onScrubStart?.();
                scrubTo(event.nativeEvent.pageX, false);
              }}
              onResponderMove={(event) => scrubTo(event.nativeEvent.pageX, false)}
              onResponderRelease={(event) => {
                scrubActive.current = false;
                scrubTo(event.nativeEvent.pageX, true);
              }}
              onResponderTerminate={() => {
                scrubActive.current = false;
                onScrubEnd?.();
                setScrub(null);
              }}
              style={styles.seekTouchTarget}
            />
            {/* Visual only: the absolute responder expands the hit area without
                contributing a single pixel to controls layout. */}
            <View style={[styles.seekTrack, { pointerEvents: "none" }]}>
              <Animated.View style={[styles.seekFillClip, { width: shownProgress }]}>
                <LinearGradient
                  colors={gradients.playback as unknown as [string, string, ...string[]]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.seekFill}
                />
              </Animated.View>
              <Animated.View style={[styles.seekHandle, { left: shownProgress }]} />
            </View>
          </View>
          <Muted
            variant="smallStrong"
            color={palette.textSoft}
            style={[styles.seekTime, styles.seekTimeRight]}
          >
            {formatDuration(duration)}
          </Muted>
        </View>

        {isFullscreen ? (
          // Fullscreen landscape: the whole transport is center-stage
          // (rendered below), so the bottom bar keeps only ambient
          // utilities on the gutter.
          <View
            style={[
              styles.controlRow,
              !onToggleVideoContentFit && styles.controlRowEnd
            ]}
          >
            {onToggleVideoContentFit ? (
              <View style={styles.controlCluster}>
                <ControlButton
                  Icon={videoContentFit === "contain" ? VideoFillIcon : VideoFitIcon}
                  label={videoContentFit === "contain" ? "Fill screen" : "Fit video"}
                  onPress={onToggleVideoContentFit}
                />
              </View>
            ) : null}
            <View style={styles.controlCluster}>
              {onOpenSettings && playbackRate != null ? (
                <RateButton rate={playbackRate} onPress={onOpenSettings} />
              ) : null}
              {onPictureInPicture ? (
                <ControlButton
                  Icon={PictureInPictureIcon}
                  label="Picture in picture"
                  onPress={onPictureInPicture}
                  iconSize={18}
                />
              ) : null}
              {onExitFullscreen ? (
                <ControlButton
                  Icon={FullscreenExitIcon}
                  label="Exit fullscreen"
                  onPress={onExitFullscreen}
                />
              ) : null}
            </View>
          </View>
        ) : remotePlayback ? (
          // The Remote's transport lives center-stage (rendered below); the
          // bottom edge keeps only the scrubber.
          null
        ) : compact ? (
          <View style={styles.controlRow}>
            <View style={styles.sideZone}>
              {onOpenSettings && playbackRate != null ? (
                <RateButton rate={playbackRate} onPress={onOpenSettings} />
              ) : null}
            </View>
            <View
              style={[
                styles.controlCluster,
                styles.centerControls,
                { pointerEvents: "box-none" }
              ]}
            >
              <ControlButton
                Icon={PreviousIcon}
                label="Previous dance"
                onPress={onPrevious}
                disabled={!hasPrevious}
              />
              <ControlButton
                Icon={playing ? PauseIcon : PlayIcon}
                label={playing ? "Pause" : "Play"}
                onPress={onTogglePlay}
                iconSize={26}
              />
              <ControlButton
                Icon={NextIcon}
                label="Next dance"
                onPress={onNext}
                disabled={!hasNext}
              />
            </View>
            <View style={[styles.controlCluster, styles.sideZone, styles.rightZone]}>
              <View style={styles.utilityControls}>
                {onPictureInPicture ? (
                  <ControlButton
                    Icon={PictureInPictureIcon}
                    label="Picture in picture"
                    onPress={onPictureInPicture}
                    iconSize={18}
                  />
                ) : null}
                {fullscreenAction ? (
                  <ControlButton
                    Icon={isFullscreen ? FullscreenExitIcon : FullscreenIcon}
                    label={fullscreenLabel}
                    onPress={fullscreenAction}
                  />
                ) : null}
              </View>
            </View>
          </View>
        ) : (
          // Wide players: play anchors the transport, the dance pair hugs
          // it, the ±10s pair sits outermost; environment utilities right.
          <View style={[styles.controlRow, styles.controlRowWide]}>
            <View style={styles.controlCluster}>
              <ControlButton
                Icon={playing ? PauseIcon : PlayIcon}
                label={playing ? "Pause" : "Play"}
                onPress={onTogglePlay}
                iconSize={26}
              />
              <ControlButton
                Icon={PreviousIcon}
                label="Previous dance"
                onPress={onPrevious}
                disabled={!hasPrevious}
              />
              <ControlButton
                Icon={NextIcon}
                label="Next dance"
                onPress={onNext}
                disabled={!hasNext}
              />
              <View style={[styles.controlCluster, styles.pairGap]}>
                <ControlButton
                  Icon={SeekBackwardIcon}
                  label="Back 10 seconds"
                  onPress={() => seekBy(-10)}
                />
                <ControlButton
                  Icon={SeekForwardIcon}
                  label="Forward 10 seconds"
                  onPress={() => seekBy(10)}
                />
              </View>
            </View>
            <View style={styles.controlCluster}>
              {onOpenSettings && playbackRate != null ? (
                <RateButton rate={playbackRate} onPress={onOpenSettings} />
              ) : null}
              {onPictureInPicture ? (
                <ControlButton
                  Icon={PictureInPictureIcon}
                  label="Picture in picture"
                  onPress={onPictureInPicture}
                  iconSize={18}
                />
              ) : null}
              {fullscreenAction ? (
                <ControlButton
                  Icon={isFullscreen ? FullscreenExitIcon : FullscreenIcon}
                  label={fullscreenLabel}
                  onPress={fullscreenAction}
                />
              ) : null}
            </View>
          </View>
        )}
      </View>

      {/* Fullscreen landscape: cinematic center-stage transport. Hit areas
          are oversized for a phone held two-handed; box-none keeps the rest
          of the screen tappable for chrome toggling. */}
      {/* The full five-slot grammar at hero scale: dance navigation hugs a
          large play, ±10s sits outermost. Hairline rings over barely-there
          glass keep every target legible on bright footage. */}
      {/* The Remote: a real remote control. Identity eyebrow over five big
          hairline-ringed buttons in the card's center — the dance name lives
          in the Now Playing card directly beneath, never here. */}
      {remotePlayback && !isFullscreen ? (
        <View
          style={[
            styles.remoteStage,
            compact && styles.remoteStageCompact,
            { pointerEvents: "box-none" }
          ]}
        >
          {remoteEyebrow ? (
            <Overline color={palette.textSoft} style={styles.remoteEyebrow}>
              {remoteEyebrow}
            </Overline>
          ) : null}
          <View
            style={[styles.remoteTransport, compact && styles.remoteTransportCompact]}
          >
            <ControlButton
              Icon={SeekBackwardIcon}
              label="Seek backward 10 seconds"
              onPress={() => seekBy(-10)}
              iconSize={compact ? 20 : 24}
              size={compact ? 44 : 54}
              circled
              veiled
            />
            <ControlButton
              Icon={PreviousIcon}
              label="Previous dance"
              onPress={onPrevious}
              disabled={!hasPrevious}
              iconSize={compact ? 22 : 26}
              size={compact ? 50 : 60}
              circled
              veiled
            />
            <ControlButton
              Icon={playing ? PauseIcon : PlayIcon}
              label={playing ? "Pause" : "Play"}
              onPress={onTogglePlay}
              iconSize={compact ? 30 : 38}
              size={compact ? 64 : 76}
              circled
              veiled
            />
            <ControlButton
              Icon={NextIcon}
              label="Next dance"
              onPress={onNext}
              disabled={!hasNext}
              iconSize={compact ? 22 : 26}
              size={compact ? 50 : 60}
              circled
              veiled
            />
            <ControlButton
              Icon={SeekForwardIcon}
              label="Seek forward 10 seconds"
              onPress={() => seekBy(10)}
              iconSize={compact ? 20 : 24}
              size={compact ? 44 : 54}
              circled
              veiled
            />
          </View>
        </View>
      ) : null}

      {isFullscreen ? (
        <View style={[styles.fullscreenTransport, { pointerEvents: "box-none" }]}>
          <ControlButton
            Icon={SeekBackwardIcon}
            label="Back 10 seconds"
            onPress={() => seekBy(-10)}
            iconSize={24}
            size={54}
            circled
          />
          <ControlButton
            Icon={PreviousIcon}
            label="Previous dance"
            onPress={onPrevious}
            disabled={!hasPrevious}
            iconSize={26}
            size={60}
            circled
          />
          <ControlButton
            Icon={playing ? PauseIcon : PlayIcon}
            label={playing ? "Pause" : "Play"}
            onPress={onTogglePlay}
            iconSize={38}
            size={76}
            circled
          />
          <ControlButton
            Icon={NextIcon}
            label="Next dance"
            onPress={onNext}
            disabled={!hasNext}
            iconSize={26}
            size={60}
            circled
          />
          <ControlButton
            Icon={SeekForwardIcon}
            label="Forward 10 seconds"
            onPress={() => seekBy(10)}
            iconSize={24}
            size={54}
            circled
          />
        </View>
      ) : null}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  chromeLayer: {
    elevation: 30,
    zIndex: 30
  },
  topScrim: {
    height: 110,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
  },
  bottomScrim: {
    bottom: 0,
    height: 140,
    left: 0,
    position: "absolute",
    right: 0
  },
  // One chrome inset on all four edges (16 compact / 24 regular) — the
  // title block, controls, and HeroCastPill must all sit on this gutter.
  // Fullscreen-only: centered between the two corner controls.
  titleBlock: {
    alignItems: "center",
    gap: 3,
    left: space.xxxl * 2,
    position: "absolute",
    right: space.xxxl * 2,
    top: space.xl
  },
  titleCentered: {
    textAlign: "center"
  },
  // Bottom inset is numerically tighter than the sides because the control
  // buttons carry ~11px of internal padding below their icons — the OPTICAL
  // bottom gap (inset + padding) is what matches the side gutters.
  controls: {
    bottom: space.md,
    left: space.xl,
    position: "absolute",
    right: space.xl
  },
  controlsCompact: {
    bottom: space.sm,
    left: space.lg,
    right: space.lg
  },
  // Remote panel: no transport row beneath the rail, so the tight inset
  // (tuned for buttons that carry their own internal padding) would leave
  // the bare scrubber glued to the rim — float it on a real gutter.
  controlsRemote: {
    bottom: space.xl
  },
  controlsRemoteCompact: {
    bottom: space.lg
  },
  seekBar: {
    flex: 1,
    justifyContent: "center",
    paddingVertical: space.xs,
    position: "relative"
  },
  seekRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm
  },
  seekTime: {
    fontVariant: ["tabular-nums"],
    minWidth: 40
  },
  // Glyphs sit flush against the chrome gutter so the title above and the
  // transport row below share one optical edge.
  seekTimeLeft: {
    textAlign: "left"
  },
  seekTimeRight: {
    textAlign: "right"
  },
  seekTouchTarget: {
    bottom: -12,
    left: 0,
    position: "absolute",
    right: 0,
    top: -12
  },
  seekTrack: {
    backgroundColor: "rgba(243, 244, 250, 0.22)",
    borderRadius: radius.pill,
    height: 4
  },
  seekFillClip: {
    borderRadius: radius.pill,
    height: 4,
    overflow: "hidden"
  },
  seekFill: {
    flex: 1
  },
  seekHandle: {
    backgroundColor: palette.text,
    borderRadius: 7,
    height: 13,
    marginLeft: -7,
    position: "absolute",
    top: -4.5,
    width: 13
  },
  controlRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    // COMPACT corner-button pull. 0 = whole button (and its hover circle)
    // inside the gutter; -11 = icon glyph flush with the gutter. Tune here.
    marginHorizontal: -6,
    minHeight: 42,
    position: "relative",
    // The seek bar intentionally has an enlarged vertical hit target. Keep
    // the adjacent transport buttons above that overlap in the touch order.
    zIndex: 1
  },
  // Wide only: pull the end buttons' internal padding out of the gutter so
  // their ICONS align with the full-bleed seek bar (hover circles may
  // overflow — desktop convention). Compact keeps whole buttons inside the
  // gutter so hover circles sit centered in the card's rounded corners.
  controlRowWide: {
    marginHorizontal: -11,
    // Clearance for the hover circles: without it they graze the seek bar.
    marginTop: 6
  },
  controlRowEnd: {
    justifyContent: "flex-end"
  },
  // Slightly wider than a pair gap: separates the dance pair from the ±10s
  // pair so the transport reads as grouped, not as one long strip.
  pairGap: {
    marginLeft: space.xs
  },
  // The Remote's center stage: identity line capping the five-slot
  // transport, nudged up a touch so the unit centers optically against the
  // corner chips above and the scrubber below.
  remoteStage: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    gap: space.xl,
    justifyContent: "center",
    transform: [{ translateY: -10 }]
  },
  // Short phone cards: center within the band the corner chips (top) and
  // the scrubber (bottom) leave free, instead of the card's true center.
  remoteStageCompact: {
    gap: space.md,
    paddingBottom: 44,
    paddingTop: 60,
    transform: []
  },
  remoteEyebrow: {
    textAlign: "center"
  },
  remoteTransport: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.xl,
    justifyContent: "center"
  },
  remoteTransportCompact: {
    gap: space.md
  },
  centerControls: {
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0
  },
  sideZone: {
    minWidth: 84
  },
  rightZone: {
    justifyContent: "flex-end"
  },
  controlCluster: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm
  },
  utilityControls: {
    alignItems: "center",
    flexDirection: "row"
  },
  fullscreenTransport: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    flexDirection: "row",
    gap: space.xl,
    justifyContent: "center"
  },
  controlButton: {
    alignItems: "center",
    borderRadius: 999,
    height: 42,
    justifyContent: "center",
    width: 42
  },
  controlButtonHovered: {
    backgroundColor: "rgba(243, 244, 250, 0.18)"
  },
  rateLabel: {
    fontVariant: ["tabular-nums"],
    letterSpacing: 0.2
  },
  // Fullscreen transport treatment: a whisper of a ring over dark glass —
  // enough to give each target a body on bright footage, never a chip.
  controlButtonCircled: {
    backgroundColor: "rgba(13, 14, 20, 0.30)",
    borderColor: "rgba(243, 244, 250, 0.20)",
    borderWidth: 1
  },
  // Remote panel: the veiled backdrop already supplies the dark; the ring
  // speaks the app's cool-hairline language and the glass barely exists.
  controlButtonVeiled: {
    backgroundColor: palette.coolWashFaint,
    borderColor: palette.hairlineStrong
  },
  controlButtonVeiledHovered: {
    backgroundColor: palette.coolWashHover,
    borderColor: "rgba(167, 178, 216, 0.35)"
  }
});
